import { toast } from "sonner";
import { formatPrice } from "@/config/pricing";
import { useSubscription } from "@/contexts/billing/subscription";
import { useCurrency } from "@/contexts/currency";
import { useRole } from "@/hooks/auth/useRole";
import { usePhoneSubscription } from "@/hooks/billing/usePhoneSubscription";
import { Agent } from "@/types/agent/agent";
import { usePhoneNumber } from "./usePhoneNumber";

const isSubscriptionTrial = (subscription: any): boolean => {
  if (!subscription) return false;
  if (subscription.status === "trialing") return true;
  if (subscription.trial_ends_at) {
    return new Date(subscription.trial_ends_at) > new Date();
  }
  // Mock/admin-created subscriptions have no Stripe attachment
  if (!subscription.stripe_id || !subscription.price_id) return true;
  return false;
};

export interface BilledProvisionParams {
  phoneNumber: string;
  friendlyName: string;
  countryCode: string;
  phoneType: "local" | "tollFree";
}

/**
 * Buys a genie's main number and adds it to the phone subscription, releasing
 * the number again if billing fails. Trials and internal admins skip billing.
 */
export const useBilledPhoneProvisioning = (genie: Agent) => {
  const { provision, release, isProvisioning, isReleasing } = usePhoneNumber(genie);
  const { addPhoneNumber, removePhoneNumber, isAddingPhone } = usePhoneSubscription();
  const { currencyConfig } = useCurrency();
  const { isSubscribed, subscription } = useSubscription();
  const { isInternalAdmin } = useRole();

  /** Resolves true once the number is live (and billed, where billing applies). */
  const provisionWithBilling = async (params: BilledProvisionParams): Promise<boolean> => {
    try {
      const provisionedPhone = await provision({
        phoneNumber: params.phoneNumber,
        friendlyName: params.friendlyName,
        agentId: genie.id,
        countryCode: params.countryCode,
      });

      const inTrial = isSubscriptionTrial(subscription);
      if (inTrial || isInternalAdmin()) {
        toast.success(
          inTrial ? "Phone number added! No charge during trial period." : "Phone number added! No charge for internal admins.",
        );
        return true;
      }

      try {
        await addPhoneNumber({ phoneNumber: params.phoneNumber, phoneType: params.phoneType, agentId: genie.id });
      } catch (billingError) {
        console.error("Error adding billing subscription:", billingError);
        try {
          await release(provisionedPhone.id);
        } catch (releaseError) {
          console.error("Failed to release phone number after billing failure:", releaseError);
        }
        throw billingError;
      }

      const price =
        params.phoneType === "local" ? currencyConfig.displayPrices.phoneLocal : currencyConfig.displayPrices.phoneTollFree;
      toast.success(`Phone number added! You'll be charged ${formatPrice(price, currencyConfig.code)}/month.`);
      return true;
    } catch (error) {
      toast.error("Failed to provision phone number");
      console.error("Error provisioning phone number:", error);
      return false;
    }
  };

  const releaseWithBilling = async (phoneNumberId: string, phoneNumber: string) => {
    await release(phoneNumberId);
    await removePhoneNumber(phoneNumber);
  };

  return {
    provisionWithBilling,
    releaseWithBilling,
    canAddPhone: isSubscribed || isInternalAdmin(),
    isProvisioning: isProvisioning || isAddingPhone,
    isReleasing,
  };
};
